(ns app.domain.application.guards.reviews
  (:require #?(:clj [core.db.entity :as entity])
            #?(:clj [datomic.api :as d])
            [app.domain.acceptance.model :as acceptance-model]
            [app.domain.application.guards.utils :as guard-utils]
            [app.domain.application.shared-core :as app-shared]
            [app.domain.attachment.model :as attachment-model]
            [app.domain.ordered.util :as ordered-util]
            [app.domain.party.model :as party-model]
            [app.domain.review.model :as review-model]
            [app.domain.util :as du]
            [clojure.set :as set]
            [core.db.entity.util :as eu]
            [potpuri.core :as p]
            [tilakone.core :as tk])
  #?(:clj (:import [clojure.lang ExceptionInfo])))

(def all-reviews-ended
  {:name    :process.guard/all-reviews-ended?
   :type    :guard
   :handler (fn [fsm]
              (let [application (-> fsm :request :target)]
                (app-shared/all-reviews-ended? application)))})

(def review-in-status?
  "The guard expects a vector of allowed states for this guard to succeed. e.g.:
   `[:process.guard/review-in-status? [:review.status/draft]]`

   Review entity id must be provided in fsm parameter :review-id"
  {:name    :process.guard/review-in-status?
   :type    :guard
   :summary "Ensure that a review is in a specific status"
   :handler (fn [fsm]
              (p/if-all-let [[_ states] (-> fsm ::tk/guard)
                             target-entity (guard-utils/find-child-entity fsm :review-id)]
                (contains? (set states) (:review/status target-entity))))})

(defn no-other-reviews-in-status-handler
  ([{[_ statuses] ::tk/guard :as fsm}]
   (no-other-reviews-in-status-handler fsm statuses #{(guard-utils/get-param fsm :review-id)}))
  ([fsm statuses no-target-reviews]
   (-> (->> fsm
            :request
            :target
            review-model/application-reviews-for-current-step
            (remove #(-> % :db/id no-target-reviews))
            (keep :review/status)
            set)
       (set/intersection (set statuses))
       empty?)))

(def no-other-reviews-in-status
  {:name    :process.guard/no-other-reviews-in-status
   :type    :guard
   :summary "Ensure that no other review under the process target is in a given status"
   :handler no-other-reviews-in-status-handler})

(def other-reviews-in-status
  {:name    :process.guard/other-reviews-in-status
   :type    :guard
   :summary "Check that some other review is in the given state"
   :handler (complement no-other-reviews-in-status-handler)})

(defn- review-for-fsm [fsm]
  (guard-utils/find-child-entity fsm :review-id))

(defn- fsm->final-review? [fsm]
  (some-> fsm review-for-fsm review-model/final-review?))

(defn- final-review-can-complete? [fsm]
  (let [review-id    (guard-utils/get-param fsm :review-id)
        final-review (-> fsm review-for-fsm :review/final-review)]
    (if final-review
      (and (review-model/all-linked-review-changes-done? final-review review-id)
           (not (review-model/own-changes-requested? final-review))
           (no-other-reviews-in-status-handler fsm
                                               #{:review.status/changes-requested}
                                               #{review-id (:db/id final-review)}))
      (no-other-reviews-in-status-handler fsm #{:review.status/changes-requested} #{review-id}))))

(def review-marks-done-final-review-too-if-exists
  {:name    :process.guard/final-review-can-complete?
   :type    :guard
   :summary "Allow completing a review when its linked Final Review can also be completed"
   :handler final-review-can-complete?})

(def final-review-cannot-complete?
  {:name    :process.guard/final-review-cannot-complete?
   :type    :guard
   :summary "Allow completing a review without also completing its linked Final Review"
   :handler (complement final-review-can-complete?)})

(def is-final-review?
  {:name    :process.guard/is-final-review?
   :type    :guard
   :summary "Check that the target review is a Final Review"
   :handler fsm->final-review?})

(defn- final-review-can-resume? [fsm]
  (let [review (review-for-fsm fsm)]
    (and (review-model/final-review? review)
         (review-model/all-linked-reviews-approved? review)
         (review-model/final-review-ready? review))))

(def final-review-can-resume
  {:name    :process.guard/final-review-can-resume?
   :type    :guard
   :summary "Select a Final Review whose completed changes can return directly to In review."
   :handler final-review-can-resume?})

(def final-review-cannot-resume
  {:name    :process.guard/final-review-cannot-resume?
   :type    :guard
   :summary "Select a review whose next round does not directly resume a Final Review."
   :handler (complement final-review-can-resume?)})

(defn- reorder-reopens-final-review? [fsm]
  (let [application  (-> fsm :request :target)
        ordering     (guard-utils/get-param fsm :reviews)
        reviews      (review-model/application-reviews-for-current-step application)
        final-review (p/find-first reviews review-model/final-review?)]
    (when (and final-review
               (review-model/final-review-released? final-review)
               ordering)
      (let [from-review (p/find-first reviews #(= (:db/id %) (:from ordering)))
            to-review   (p/find-first reviews #(= (:db/id %) (:to ordering)))]
        (try
          (let [reorder     (ordered-util/order-fn from-review to-review)
                final-order (reorder final-review)]
            (some (fn [review]
                    (and (not (review-model/final-review? review))
                         (not= (review-model/final-review-id review) (:db/id final-review))
                         (< (reorder review) final-order)))
                  reviews))
          (catch #?(:clj ExceptionInfo :cljs js/Error) _ false))))))

(def reorder-reopens-final-review
  {:name    :process.guard/reorder-reopens-final-review
   :type    :guard
   :summary "Check that reordering reviews starts a new Final Review round"
   :handler reorder-reopens-final-review?})

(def not-reorder-reopens-final-review
  {:name    :process.guard/not-reorder-reopens-final-review
   :type    :guard
   :summary "Check that reordering reviews does not start a new Final Review round"
   :handler (complement reorder-reopens-final-review?)})

(defn- application-submitted? [fsm]
  (= :process.state/submitted (-> fsm :request :target :process/state)))

(def application-not-submitted?
  "State-changing Final Review signals are available only outside the submitted process state."
  {:name    :process.guard/application-not-submitted?
   :type    :guard
   :summary "Check that the application is not submitted"
   :handler (complement application-submitted?)})

(defn- application-in-changes-requested-state? [fsm]
  (contains? #{:state.application/changes-requested
               :state.application/submission-changes-requested}
             (-> fsm :request :target :domain/state)))

(def ordinary-reorder-allowed?
  "Allow the ordinary reorder signal unless the reorder reopens a released Final Review in a changes-requested state."
  {:name    :process.guard/ordinary-reorder-allowed?
   :type    :guard
   :summary "Check that reordering does not reopen a Final Review in a changes-requested state"
   :handler (fn [fsm]
              (or (not (reorder-reopens-final-review? fsm))
                  (not (application-in-changes-requested-state? fsm))))})

(def not-final-review?
  {:name    :process.guard/not-final-review?
   :type    :guard
   :summary "Check that the target review is not a Final Review"
   :handler (complement fsm->final-review?)})

(defn- fsm->linked-to-incomplete-final-review? [fsm]
  (some-> fsm review-for-fsm review-model/linked-to-incomplete-final-review?))

(def linked-to-incomplete-final-review?
  {:name    :process.guard/linked-to-incomplete-final-review?
   :type    :guard
   :summary "Check that the target review result is held by an incomplete Final Review"
   :handler fsm->linked-to-incomplete-final-review?})

(defn- fsm->linked-to-released-final-review? [fsm]
  (some-> fsm review-for-fsm :review/final-review review-model/final-review-released?))

(defn- no-unrelated-review-changes-requested-handler [fsm]
  (let [review         (review-for-fsm fsm)
        final-review   (:review/final-review review)
        linked-reviews (review-model/reviews-linked-to-final final-review)
        related-ids    (-> linked-reviews
                           du/entity-id-set
                           (conj (:db/id review) (:db/id final-review)))]
    (no-other-reviews-in-status-handler fsm #{:review.status/changes-requested} related-ids)))

(def no-unrelated-review-changes-requested?
  {:name    :process.guard/no-unrelated-review-changes-requested?
   :type    :guard
   :summary "Check that no review outside the reopened Final Review chain requests changes"
   :handler no-unrelated-review-changes-requested-handler})

(def unrelated-review-changes-requested?
  {:name    :process.guard/unrelated-review-changes-requested?
   :type    :guard
   :summary "Check that a review outside the reopened Final Review chain requests changes"
   :handler (complement no-unrelated-review-changes-requested-handler)})

(def linked-to-released-final-review?
  {:name    :process.guard/linked-to-released-final-review?
   :type    :guard
   :summary "Check that the target review is linked to a released Final Review"
   :handler fsm->linked-to-released-final-review?})

(def not-linked-to-released-final-review?
  {:name    :process.guard/not-linked-to-released-final-review?
   :type    :guard
   :summary "Check that the target review is not linked to a released Final Review"
   :handler (complement fsm->linked-to-released-final-review?)})

(def ordinary-edit-allowed?
  "Allow the ordinary edit signal unless the review is linked to a released Final Review and the application is in a
   changes-requested state."
  {:name    :process.guard/ordinary-edit-allowed?
   :type    :guard
   :summary "Check that the review is not linked to a released Final Review or the application is not changes-requested"
   :handler (fn [fsm]
              (or (not (fsm->linked-to-released-final-review? fsm))
                  (not (application-in-changes-requested-state? fsm))))})

(def not-linked-to-incomplete-final-review?
  {:name    :process.guard/not-linked-to-incomplete-final-review?
   :type    :guard
   :summary "Check that the target review result is not held by an incomplete Final Review"
   :handler (complement fsm->linked-to-incomplete-final-review?)})

(def final-review-ready?
  {:name    :process.guard/final-review-ready?
   :type    :guard
   :summary "Check that a Final Review's linked reviews have all ended"
   :handler #(some-> % review-for-fsm review-model/final-review-ready?)})

(def all-linked-reviews-approved?
  {:name    :process.guard/all-linked-reviews-approved?
   :type    :guard
   :summary "Check that every review linked to the Final Review was approved"
   :handler #(some-> % review-for-fsm review-model/all-linked-reviews-approved?)})

(def all-linked-review-changes-done?
  {:name    :process.guard/all-linked-review-changes-done?
   :type    :guard
   :summary "Check that all linked Final Review changes have been marked done"
   :handler #(some-> % review-for-fsm review-model/all-linked-review-changes-done?)})

(def all-linked-reviews-completed?
  {:name    :process.guard/all-linked-reviews-completed?
   :type    :guard
   :summary "Check that every review linked to the Final Review has completed"
   :handler #(some-> % review-for-fsm review-model/all-linked-reviews-completed?)})

(defn- route-to-changes-requested-handler [{:as fsm}]
  (and (#{:process.state/in-review :process.state/submitted} (-> fsm :request :target :process/state))
       (no-other-reviews-in-status-handler
         (assoc fsm ::tk/guard [:process.guard/no-other-reviews-in-status #{:review.status/in-review}]))
       (not (no-other-reviews-in-status-handler
              (assoc fsm ::tk/guard [:process.guard/no-other-reviews-in-status #{:review.status/changes-requested}])))))

(def route-to-changes-requested?
  {:name    :process.guard/route-to-changes-requested?
   :type    :guard
   :summary "Check whether completing this review must move the workspace to Changes Requested"
   :handler route-to-changes-requested-handler})

(def not-route-to-changes-requested?
  {:name    :process.guard/not-route-to-changes-requested?
   :type    :guard
   :summary "Check whether completing this review must not move the workspace to Changes Requested"
   :handler (complement route-to-changes-requested-handler)})

(def review-time-given-if-required?
  {:name    :process.guard/review-time-given-if-required?
   :type    :guard
   :summary "Check that review minutes spent is given if review time tracking feature is on."
   :handler (fn [{[_ params] ::tk/signal :as fsm}]
              (if (guard-utils/group-feature-available? fsm :group.feature/review-time-tracking)
                (-> params :sub-review/minutes-spent pos-int?)
                true))})

(def review-open?
  {:name    :process.guard/review-open?
   :type    :guard
   :summary "Check that the review has not ended."
   :handler (fn [fsm]
              (let [review (guard-utils/find-child-entity fsm :review-id)]
                (-> review :review/end nil?)))})

(def attachment-no-markup-versions?
  {:name    :process.guard/attachment-no-markup-versions?
   :type    :guard
   :summary "Check if the attachment has markup versions"
   :handler (fn [fsm]
              (->> (guard-utils/find-child-entity fsm :attachment-id)
                   :attachment/versions
                   (not-any? :attachment.version/authority-markup?)))})

(defn acceptances-for-review-items [fsm]
  (let [review               (guard-utils/find-child-entity fsm :review-id)
        relevant-acceptance? (-> review :review/acceptances set)]
    (->> (concat (:review/attachments review)
                 (:review/forms review))
         (map (juxt identity acceptance-model/acceptances-for-entity))
         (map (fn [[_ acceptances]]
                (->> acceptances
                     (filter relevant-acceptance?)
                     du/latest-entity))))))

(def all-selected-items-have-acceptances?
  {:name    :process.guard/all-selected-items-have-acceptances?
   :type    :guard
   :summary "Check that all attachments and forms selected for review either have been accepted or have a new draft comment"
   :handler (fn [fsm]
              (->> (acceptances-for-review-items fsm)
                   (every? (some-fn (fn [acceptance]
                                      (and (du/in-state? :acceptance.state/draft acceptance)
                                           (-> acceptance :acceptance/value some?)))
                                    (acceptance-model/has-value-fn :acceptance.value/ok)))))})

(def all-items-accepted?
  {:name    :process.guard/all-items-accepted?
   :type    :guard
   :summary "Check that all attachments and forms selected for review have been accepted"
   :handler (fn [fsm]
              (->> (acceptances-for-review-items fsm)
                   (every? (acceptance-model/has-value-fn :acceptance.value/ok))))})

(def target-does-not-have-draft-acceptance?
  {:name    :process.guard/target-does-not-have-draft-acceptance?
   :type    :guard
   :summary "Check that target has no acceptances in draft state"
   :handler (fn [{[_ {:keys [review-id]}] ::tk/signal
                  :as                     fsm}]
              (let [target     (guard-utils/find-child-entity fsm :target-id)
                    attachment (when (and target
                                          (eu/is-type? :entity.type/attachment-version target))
                                 (attachment-model/attachment-for-version target))]
                ;; In Bluebeam flow, draft acceptances may be intentionally
                ;; retained (see skip-draft-acceptance-retract?) and markup versions
                ;; via attachment version signal are created without review-id.
                ;; Use session-id as the signal for this exception.
                (or (some? (:attachment/session-id attachment))
                    (->> target
                         acceptance-model/acceptances-for-entity
                         (filter (if review-id
                                   #(-> % :review/_acceptances (contains? review-id))
                                   (constantly true)))
                         (not-any? (du/in-state-fn :acceptance.state/draft))))))})

(def all-acceptances-responded?
  {:name    :process.guard/all-acceptances-responded?
   :type    :guard
   :summary "All non-ok acceptances have a customer response"
   :handler (fn [fsm]
              (->> (acceptances-for-review-items fsm)
                   (filter (acceptance-model/has-value-fn :acceptance.value/needs-changes))
                   (every? (acceptance-model/has-customer-value-fn :acceptance.customer-value/updated-by-customer))))})

(def acceptance-belongs-to-target?
  {:name    :process.guard/acceptance-belongs-to-target?
   :type    :guard
   :summary (str "Acceptances are not part of the normal entity child tree, so a special check is needed to ensure "
                 "the specified acceptance is related to the workspace in question")
   :handler (fn [#?(:cljs _
                    :clj {[_ {:keys [id]}] ::tk/signal
                          {:keys [target]} :request
                          :keys            [:core.db/db]})]
              #?(:cljs true
                 ;; This guard only works in the backend, because it's difficult to find that entity
                 ;; from the frontend entity-db. However, this situation does not arise in the frontend,
                 ;; only protects from curling attacks.
                 :clj (-> (d/entity db id)
                          acceptance-model/acceptance-parent
                          (entity/=-or-descendant-of? target))))})

(def all-attachment-requests-fulfilled?
  {:name    :process.guard/all-attachment-requests-fulfilled?
   :type    :guard
   :summary "All attachment requirements related to a review have a related attachment uploaded"
   :handler (fn [fsm]
              (let [fsm-target       (-> fsm :request :target)
                    review           (guard-utils/find-child-entity fsm :review-id)
                    requested-types  (->> review
                                          :review/requirements
                                          (keep #(-> % :requirement/attachment-type :db/id))
                                          (into #{}))
                    attachment-types (->> (attachment-model/phase-attachments fsm-target)
                                          (mapcat :attachment/types)
                                          du/entity-id-set)]
                (empty? (set/difference requested-types attachment-types))))})

(def all-party-role-requests-fulfilled?
  {:name    :process.guard/all-party-role-requests-fulfilled?
   :type    :guard
   :summary "All party role requirements related to a review have a related party attached"
   :handler (fn [fsm]
              (let [fsm-target      (-> fsm :request :target)
                    review          (guard-utils/find-child-entity fsm :review-id)
                    requested-roles (->> review
                                         :review/requirements
                                         (keep #(-> % :requirement/party-role :db/id))
                                         (into #{}))
                    available-roles (->> (party-model/active-parties fsm-target)
                                         (mapcat :party/party-roles)
                                         du/entity-id-set)]
                (empty? (set/difference requested-roles available-roles))))})

(def review-includes-attachments?
  {:name    :process.guard/review-includes-attachments?
   :type    :guard
   :summary "Check that review has at least one attachment"
   :handler (fn [fsm]
              (->> (guard-utils/find-child-entity fsm :review-id)
                   :review/attachments
                   seq
                   boolean))})

(def review-attachments-not-locked?
  {:name    :process.guard/review-attachments-not-locked?
   :type    :guard
   :summary "Check that no attachments in the review are locked for external editing"
   :handler (fn [fsm]
              (->> (guard-utils/find-child-entity fsm :review-id)
                   :review/attachments
                   (every? #(nil? (:attachment/locked-for-editing-by %)))))})

(def review-assigned-to-me?
  {:name    :process.guard/review-assigned-to-me?
   :type    :guard
   :summary "Require review to be assigned to the current user"
   :handler (fn [{user :core.user/user
                  :as  fsm}]
              (-> (guard-utils/find-child-entity fsm :review-id)
                  :review/assignee
                  :db/id
                  (= (:db/id user))))})

(def begin-review-permitted?
  {:name    :process.guard/begin-review-permitted?
   :type    :guard
   :summary "Require the current user to be allowed to claim the review when beginning it"
   :handler (fn [{user                    :core.user/user
                  {:keys [feature-flags]} :request
                  :as                     fsm}]
              (let [assignment-groups-enabled? (contains? feature-flags :feature-flag.id/review-assignment-groups)]
                (some-> (guard-utils/find-child-entity fsm :review-id)
                        (review-model/begin-review-permitted? assignment-groups-enabled? user))))})

(def confirm-review-permitted?
  {:name    :process.guard/confirm-review-permitted?
   :type    :guard
   :summary "Automatic due-date reviews skip confirmation"
   :handler (fn [{{:keys [feature-flags]} :request :as fsm}]
              (some-> (guard-utils/find-child-entity fsm :review-id)
                      :review/review-config
                      (review-model/automatic-due-date? (guard-utils/get-authority-group fsm) feature-flags)
                      not))})

(def attachment-not-locked?
  {:name    :process.guard/attachment-not-locked?
   :type    :guard
   :summary (str "Check that attachment is not be locked for editing. Looks either for attachment in :attachment-id"
                 " or attachment version in :target-id for use with reviews. Can be overridden with :ignore-lock-status?")
   :handler (fn [fsm]
              (or (guard-utils/get-param fsm :ignore-lock-status?)
                  (let [attachment (or (guard-utils/find-child-entity fsm :attachment-id)
                                       (some-> (guard-utils/find-child-entity fsm :target-id)
                                               (eu/assure-entity-type :entity.type/attachment-version)
                                               :attachment/_versions))]
                    (not (:attachment/locked-for-editing-by attachment)))))})

(def attachment-locked?
  {:name    :process.guard/attachment-locked?
   :type    :guard
   :summary "Check that attachment is currently locked for editing. Note: less versatile than attachment-not-locked?"
   :handler (fn [fsm]
              (-> (guard-utils/find-child-entity fsm :attachment-id)
                  :attachment/locked-for-editing-by
                  boolean))})

(def external-plan-review-configured?
  {:name    :process.guard/external-plan-review-configured?
   :type    :guard
   :summary "Check that group has the feature and config available for external plan review tool (either DigEplan or Bluebeam)"
   :handler (fn [fsm]
              (and (or (guard-utils/group-feature-available? fsm :group.feature/external-plan-review-integration)
                       (guard-utils/group-feature-available? fsm :group.feature/enable-bluebeam))
                   (-> fsm
                       :request
                       :target
                       :entity/parent
                       :domain/authority-group
                       :group/plan-review-integration-config
                       some?)))})

(def review-integration-id
  {:name    :process.guard/review-integration-id
   :type    :guard
   :summary "Check that plan review integration service matches the expected :integration-config/service keyword (e.g. :integration-config.service/bluebeam)"
   :handler (fn [fsm]
              (let [[_ expected-service] (-> fsm ::tk/guard)
                    configured-service   (-> fsm
                                             :request
                                             :target
                                             :entity/parent
                                             :domain/authority-group
                                             :group/plan-review-integration-config
                                             :integration-config/service)]
                (= expected-service configured-service)))})


(def review-created-in-current-step?
  "Returns true when the review is in the same review step as the application.
   Reviews created in :process.state/submitted match only while the application is in
   :process.state/submitted. Reviews without :review/created-in-state, or with any
   other value, are treated as in-review step reviews."
  {:name    :process.guard/review-created-in-current-step?
   :type    :guard
   :summary "Ensure that the review belongs to the application's current review step"
   :handler (fn [fsm]
              (p/if-all-let [review (guard-utils/find-child-entity fsm :review-id)
                             target (-> fsm :request :target)]
                (or (app-shared/review-created-in-current-step? target review)
                    (throw (ex-info "Review belongs to another step"
                                    {:error-translation-keys
                                     [(case (app-shared/normalized-review-created-state
                                              (:review/created-in-state review))
                                        :process.state/submitted :state.application/submitted
                                        :state.application/in-review)]})))))})

(def guards
  [all-reviews-ended
   no-other-reviews-in-status
   other-reviews-in-status
   review-marks-done-final-review-too-if-exists
   final-review-cannot-complete?
   is-final-review?
   final-review-can-resume
   final-review-cannot-resume
   reorder-reopens-final-review
   not-reorder-reopens-final-review
   application-not-submitted?
   ordinary-reorder-allowed?
   not-final-review?
   linked-to-incomplete-final-review?
   linked-to-released-final-review?
   not-linked-to-released-final-review?
   no-unrelated-review-changes-requested?
   unrelated-review-changes-requested?
   ordinary-edit-allowed?
   not-linked-to-incomplete-final-review?
   final-review-ready?
   all-linked-reviews-approved?
   all-linked-review-changes-done?
   all-linked-reviews-completed?
   route-to-changes-requested?
   not-route-to-changes-requested?
   review-in-status?
   review-created-in-current-step?
   review-time-given-if-required?
   review-open?
   attachment-no-markup-versions?
   all-selected-items-have-acceptances?
   all-items-accepted?
   target-does-not-have-draft-acceptance?
   all-acceptances-responded?
   acceptance-belongs-to-target?
   all-attachment-requests-fulfilled?
   all-party-role-requests-fulfilled?
   review-includes-attachments?
   review-assigned-to-me?
   begin-review-permitted?
   confirm-review-permitted?
   attachment-not-locked?
   attachment-locked?
   external-plan-review-configured?
   review-integration-id
   review-attachments-not-locked?])
